import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { FormattedInput } from '@/components/ui/formatted-input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Card, CardContent } from '@/components/ui/card';
import {
  CalendarIcon,
  CircleNotchIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import SelectDropdown, { SelectDropdownOption } from '../common/SelectDropdown';
import FileUploadDropzone from '../documents/FileUploadDropzone';
import FileIcon from '../documents/FileIcon';
import { useCreateSupplierPayment } from '../../api/rebates/useSupplierPayments';
import { CreateSupplierPaymentRequest } from '../../api/rebates/types';
import {
  RebateSupplierPaymentType,
  REBATE_SUPPLIER_PAYMENT_TYPE_LABELS,
} from '@erp/shared-types';
import { useToast } from 'src/context/ToastContext';
import { UploadProgress } from 'src/types/UserDocuments';
import { formatCurrency } from '@erp/shared-utils';

interface SupplierPaymentModalProps {
  open: boolean;
  onClose: () => void;
  agreementId: string;
  onSuccess: () => void;
  defaultTaxRate?: number; // Optional default tax rate from company metadata
}
// Payment type options - generated from shared enum and labels
const paymentTypeOptions: SelectDropdownOption[] = Object.values(
  RebateSupplierPaymentType,
).map((type) => ({
  id: type,
  display: REBATE_SUPPLIER_PAYMENT_TYPE_LABELS[type],
  label: REBATE_SUPPLIER_PAYMENT_TYPE_LABELS[type],
  value: type,
}));

const SupplierPaymentModal: React.FC<SupplierPaymentModalProps> = ({
  open,
  onClose,
  agreementId,
  onSuccess,
  defaultTaxRate,
}) => {
  const [formData, setFormData] = useState({
    date: new Date(), // Use Date object for internal state
    grossAmount: '', // User enters gross amount
    type: RebateSupplierPaymentType.CHECK,
    rebateAgreementId: agreementId,
    memo: '',
    attachment: undefined as File | undefined,
  });

  const [applyTax, setApplyTax] = useState(false);
  const [taxRate, setTaxRate] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [selectedPaymentType, setSelectedPaymentType] =
    useState<SelectDropdownOption | null>(null);

  const createMutation = useCreateSupplierPayment();
  const { showToast } = useToast();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      const defaultPaymentType =
        paymentTypeOptions.find(
          (option) => option.value === RebateSupplierPaymentType.CHECK,
        ) || null;
      setFormData({
        date: new Date(),
        grossAmount: '',
        type: RebateSupplierPaymentType.CHECK,
        rebateAgreementId: agreementId,
        memo: '',
        attachment: undefined,
      });
      setErrors({});
      setSelectedFiles([]);
      setUploadProgress([]);
      setSelectedPaymentType(defaultPaymentType);

      // Set default tax rate if provided
      if (defaultTaxRate !== undefined && defaultTaxRate > 0) {
        setApplyTax(true);
        setTaxRate(defaultTaxRate.toString());
      } else {
        setApplyTax(false);
        setTaxRate('');
      }
    }
  }, [open, agreementId, defaultTaxRate]);

  // Calculate tax breakdown for withholding tax
  // User enters GROSS amount → calculate tax withheld → calculate NET amount received
  const calculateTaxBreakdown = () => {
    const gross = parseFloat(formData.grossAmount) || 0;

    if (!applyTax || !taxRate || gross === 0) {
      return {
        grossAmount: gross,
        taxWithheld: 0,
        netAmount: gross,
      };
    }

    const rate = parseFloat(taxRate) || 0;
    const taxWithheld = gross * (rate / 100);
    const netAmount = gross - taxWithheld;

    return {
      grossAmount: Math.round(gross * 100) / 100,
      taxWithheld: Math.round(taxWithheld * 100) / 100,
      netAmount: Math.round(netAmount * 100) / 100,
    };
  };

  const taxBreakdown = calculateTaxBreakdown();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'Payment date is required';
    }

    const grossAmount = parseFloat(formData.grossAmount);
    if (!formData.grossAmount || isNaN(grossAmount) || grossAmount <= 0) {
      newErrors.grossAmount = 'Gross rebate amount must be greater than 0';
    }

    if (!formData.type) {
      newErrors.type = 'Payment type is required';
    }

    if (applyTax) {
      const rate = parseFloat(taxRate);
      if (!taxRate || isNaN(rate) || rate < 0 || rate > 100) {
        newErrors.taxRate = 'Tax rate must be between 0 and 100';
      }
    }

    if (selectedFiles.length > 0) {
      const file = selectedFiles[0];
      if (file.size > 10 * 1024 * 1024) {
        newErrors.attachment = 'File size must be less than 10MB';
      }

      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
      ];
      if (!allowedTypes.includes(file.type)) {
        newErrors.attachment = 'Only PDF, JPG, JPEG, and PNG files are allowed';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFilesSelected = useCallback((files: File[]) => {
    // Only allow one file for payment attachments
    const file = files[0];
    if (file) {
      setSelectedFiles([file]);
      setFormData((prev) => ({ ...prev, attachment: file }));
      // Clear any previous attachment errors
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.attachment;
        return newErrors;
      });
    }
  }, []);

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({ ...prev, attachment: undefined }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Calculate final values - NET amount is what we actually receive
    const breakdown = calculateTaxBreakdown();
    const submitData: CreateSupplierPaymentRequest = {
      date: formData.date.toISOString().split('T')[0], // Convert Date to YYYY-MM-DD string
      amount: applyTax ? breakdown.netAmount : breakdown.grossAmount, // amount is NET received
      type: formData.type,
      rebateAgreementId: formData.rebateAgreementId,
      memo: formData.memo,
      attachment: selectedFiles[0] || undefined,
    };

    // Add tax fields if tax is applied
    if (applyTax && taxRate) {
      const rate = parseFloat(taxRate);
      submitData.grossAmount = breakdown.grossAmount;
      submitData.taxRate = rate;
      submitData.taxAmount = breakdown.taxWithheld;
    }

    try {
      await createMutation.mutateAsync(submitData);

      onSuccess();
      onClose();
      showToast({
        message: 'Payment record created successfully',
        title: 'Success',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      console.error('Error creating supplier payment:', error);
      showToast({
        message: 'Failed to create payment record',
        title: 'Error',
        color: 'error',
        icon: 'warning',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[95vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Record Supplier Payment</DialogTitle>
          <DialogDescription>
            Create a record of a payment made to the supplier for this rebate
            agreement.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 px-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // Prevent accidental form submission on Enter key
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Payment Date */}
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${
                        errors.date ? 'border-red-500' : ''
                      }`}
                      disabled={createMutation.isLoading}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date
                        ? format(formData.date, 'PPP')
                        : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      defaultMonth={formData.date}
                      onSelect={(date) =>
                        setFormData((prev) => ({
                          ...prev,
                          date: date || new Date(),
                        }))
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {errors.date && (
                  <p className="text-sm text-red-600">{errors.date}</p>
                )}
              </div>

              {/* Gross Rebate Amount */}
              <div className="space-y-2">
                <Label htmlFor="grossAmount">Gross Rebate Amount *</Label>
                <FormattedInput
                  id="grossAmount"
                  type="text"
                  value={formData.grossAmount}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      grossAmount: value,
                    }))
                  }
                  formatType="currency"
                  className={errors.grossAmount ? 'border-red-500' : ''}
                  placeholder="0.00"
                  disabled={createMutation.isLoading}
                />
                {errors.grossAmount && (
                  <p className="text-sm text-red-600">{errors.grossAmount}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  The rebate amount owed by the supplier before tax withholding
                </p>
              </div>
            </div>

            {/* Tax Information */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="applyTax"
                  checked={applyTax}
                  onCheckedChange={(checked) => setApplyTax(checked as boolean)}
                  disabled={createMutation.isLoading}
                />
                <Label
                  htmlFor="applyTax"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Apply Tax
                </Label>
              </div>

              {applyTax && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="taxRate">Tax Rate (%)</Label>
                    <FormattedInput
                      id="taxRate"
                      type="text"
                      value={taxRate}
                      onChange={(value) => setTaxRate(value)}
                      formatType="percentage"
                      className={errors.taxRate ? 'border-red-500' : ''}
                      placeholder="e.g., 13"
                      disabled={createMutation.isLoading}
                    />
                    {errors.taxRate && (
                      <p className="text-sm text-red-600">{errors.taxRate}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Enter the tax rate percentage (e.g., 13 for 13%)
                    </p>
                  </div>

                  {/* Tax Breakdown Preview */}
                  {formData.grossAmount && taxRate && (
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-4 space-y-2">
                        <p className="text-sm font-semibold text-blue-900">
                          Withholding Tax Calculation:
                        </p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-700">Gross Amount:</span>
                            <span className="font-medium">
                              {formatCurrency(taxBreakdown.grossAmount)}
                            </span>
                          </div>
                          <div className="flex justify-between text-red-700">
                            <span>Tax Withheld ({taxRate}%):</span>
                            <span className="font-medium">
                              -{formatCurrency(taxBreakdown.taxWithheld)}
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                            <span className="font-semibold text-gray-900">
                              Net Payment Received:
                            </span>
                            <span className="font-semibold text-green-700">
                              {formatCurrency(taxBreakdown.netAmount)}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>

            {/* Payment Type */}
            <div className="space-y-2">
              <SelectDropdown
                options={paymentTypeOptions}
                value={selectedPaymentType}
                onChange={(selected) => {
                  setSelectedPaymentType(selected);
                  if (selected) {
                    setFormData((prev) => ({
                      ...prev,
                      type: selected.value,
                    }));
                  }
                }}
                label="Payment Type"
                placeholder="Select payment type..."
                error={!!errors.type}
                helperText={errors.type || 'Select the type of payment'}
                required
              />
            </div>

            {/* Memo */}
            <div className="space-y-2">
              <Label htmlFor="memo">Memo (Optional)</Label>
              <Textarea
                id="memo"
                value={formData.memo || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, memo: e.target.value }))
                }
                placeholder="Add a memo or note about this payment..."
                rows={3}
                maxLength={500}
                disabled={createMutation.isLoading}
              />
              <p className="text-sm text-muted-foreground">
                Optional memo about this payment (max 500 characters)
              </p>
            </div>

            {/* File Attachment */}
            <div className="space-y-2">
              <Label>Attachment (Optional)</Label>
              <p className="text-sm font-semibold text-gray-900">
                Upload up to 1 file (PDF, JPG, JPEG, or PNG)
              </p>
              <FileUploadDropzone
                onFilesSelected={handleFilesSelected}
                uploadProgress={uploadProgress}
                isUploading={createMutation.isLoading}
                disabled={createMutation.isLoading}
                maxFiles={1}
              />
              {errors.attachment && (
                <p className="text-sm text-red-600">{errors.attachment}</p>
              )}
            </div>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Selected File</Label>
                {selectedFiles.map((file, index) => (
                  <Card key={index}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileIcon
                            fileName={file.name}
                            size="small"
                            showBackground={false}
                          />
                          <div>
                            <p className="text-sm font-medium">{file.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFile(index)}
                          disabled={createMutation.isLoading}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </form>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={createMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createMutation.isLoading}
            className="min-w-32"
          >
            {createMutation.isLoading ? (
              <>
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Create Payment Record'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierPaymentModal;
